/**
 * Week-at-a-glance for a hub, built from js/day-events.js (generated from the seed).
 * Only plain weekly rows ("Mondays", "Daily", "Tuesdays, Thursdays") go on a weekday.
 * Monthly or alternating rows are listed apart because the seed does not say which week.
 * Guarantees come only from the event name on file; nothing is computed or typed by hand.
 */
var events = require('./day-events');
var dayMatch = require('./day-match');

var WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

function startKey(start) {
  var s = String(start || '').trim().toLowerCase();
  var m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?/);
  if (!m) return 9999;
  var h = parseInt(m[1], 10);
  var min = m[2] ? parseInt(m[2], 10) : 0;
  var ap = m[3] || '';
  if (ap.indexOf('p') === 0 && h < 12) h += 12;
  if (ap.indexOf('a') === 0 && h === 12) h = 0;
  return h * 60 + min;
}

// "$30K GTD", "15K GTD", "$5,000 GTD", "$500 GTD" -> dollars. Null when the name has no guarantee.
function gtdAmount(name) {
  var m = String(name || '').replace(/,/g, '').match(/\$?\s*(\d+(?:\.\d+)?)\s*(k)?\s*gtd/i);
  if (!m) return null;
  var n = parseFloat(m[1]);
  if (m[2]) n *= 1000;
  return n;
}

function isFree(buyin) {
  var s = String(buyin || '').trim().toLowerCase();
  return s === '$0' || s === '0' || s === 'free';
}

function weekdaysFor(dayStr) {
  var s = String(dayStr || '').trim();
  if (!s) return null;
  if (/^daily/i.test(s)) return WEEK_ORDER.slice();
  if (/\balternating\b/i.test(s) || dayMatch.isQualified(s)) return null;
  var hits = [];
  WEEK_ORDER.forEach(function(index) {
    var re = new RegExp('\\b' + dayMatch.WEEKDAYS[index] + 's?\\b', 'i');
    if (re.test(s)) hits.push(index);
  });
  return hits.length ? hits : null;
}

function byStart(a, b) {
  var c = startKey(a.start) - startKey(b.start);
  if (c) return c;
  return a.casino.localeCompare(b.casino);
}

function build(roomNames) {
  var wanted = {};
  (roomNames || []).forEach(function(name) { wanted[name] = true; });
  var days = {};
  WEEK_ORDER.forEach(function(index) { days[index] = []; });
  var monthly = [];
  var rooms = {};
  var all = [];
  (events || []).forEach(function(ev) {
    if (!wanted[ev.casino]) return;
    var row = {
      casino: ev.casino,
      name: ev.name || '',
      start: ev.start || '',
      buyin: ev.buyin || '',
      gtd: gtdAmount(ev.name),
      dayLabel: ev.day
    };
    rooms[ev.casino] = true;
    all.push(row);
    var list = weekdaysFor(ev.day);
    if (!list) {
      monthly.push(row);
      return;
    }
    list.forEach(function(index) { days[index].push(row); });
  });
  var weekly = WEEK_ORDER.map(function(index) {
    var list = days[index].slice().sort(byStart);
    var roomSet = {};
    var top = null;
    list.forEach(function(row) {
      roomSet[row.casino] = true;
      if (row.gtd !== null && (!top || row.gtd > top.gtd)) top = row;
    });
    return {
      index: index,
      day: dayMatch.WEEKDAYS[index],
      events: list,
      roomCount: Object.keys(roomSet).length,
      rooms: Object.keys(roomSet).sort(),
      topGtd: top
    };
  });
  return {
    days: weekly,
    monthly: monthly.sort(function(a, b) { return a.casino.localeCompare(b.casino); }),
    roomCount: Object.keys(rooms).length,
    all: all,
    eventCount: weekly.reduce(function(sum, d) { return sum + d.events.length; }, 0)
  };
}

function firstAmount(text) {
  var m = String(text || '').replace(/,/g, '').match(/\$\s*(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

// One or two sentences of counts from the rows on file. Nothing is typed by hand.
function facts(week, areaLabel) {
  var rows = week.all;
  if (!rows.length) return '';
  var amounts = [];
  var free = 0;
  var gtd = 0;
  rows.forEach(function(row) {
    if (isFree(row.buyin)) { free++; } else {
      var amt = firstAmount(row.buyin);
      if (amt !== null && amt > 0) amounts.push(amt);
    }
    if (row.gtd !== null) gtd++;
  });
  var s = rows.length + ' recurring tournaments are on file at ' + week.roomCount + ' ' + areaLabel + ' rooms';
  if (amounts.length) {
    s += ', with buy-ins from $' + Math.min.apply(null, amounts) + ' to $' + Math.max.apply(null, amounts);
  }
  s += '.';
  var extra = [];
  if (free) extra.push(free + (free === 1 ? ' is' : ' are') + ' free to enter');
  if (gtd) extra.push(gtd + ' name a guaranteed prize pool');
  if (extra.length) s += ' Of those, ' + extra.join(' and ') + '.';
  return s;
}

// "7:00pm · $75" style detail for one row, skipping blanks.
function detail(row) {
  var bits = [];
  if (row.start) bits.push(row.start);
  if (row.buyin) bits.push(isFree(row.buyin) ? 'free entry (club fees may apply)' : row.buyin + ' buy-in');
  return bits.join(' · ');
}

module.exports = {
  build: build,
  facts: facts,
  detail: detail,
  gtdAmount: gtdAmount,
  isFree: isFree,
  startKey: startKey,
  weekdaysFor: weekdaysFor,
  WEEK_ORDER: WEEK_ORDER
};

if (require.main === module) {
  function assert(cond, msg) { if (!cond) throw new Error(msg); }
  assert(gtdAmount('Palace Powerstack $30K GTD') === 30000, '30k');
  assert(gtdAmount('15K GTD Friday Unchained NLH') === 15000, '15k no dollar');
  assert(gtdAmount('$5,000 GTD Friday Double Board PLO $50 Entry') === 5000, '5,000');
  assert(gtdAmount('$100 Freezeout') === null, 'no gtd');
  assert(weekdaysFor('Daily').length === 7, 'daily');
  assert(weekdaysFor('Tuesdays, Thursdays').join() === '2,4', 'tue thu');
  assert(weekdaysFor('Sundays (third each month)') === null, 'monthly');
  assert(weekdaysFor('2nd Saturday of the month') === null, '2nd sat');
  assert(startKey('10:30am') < startKey('12:00pm') && startKey('12:00pm') < startKey('1:00pm'), 'sort');
  console.log('hub-week ok');
}



// WEBPACK FOOTER //
// ./js/hub-week.js